import { Injectable, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { AtomBulkActionBarIntl } from "@atomchat-io/ui-design-system";
import { TranslocoService } from "@jsverse/transloco";
import { combineLatest, switchMap, take } from "rxjs";

/**
 * The `atom_bulk_action_bar` block of the catalogs. Typed explicitly because
 * `selectTranslateObject` defaults its generic to `any`, and that `any` would
 * end up as `getSelectedLabel`'s return type.
 */
interface BulkActionBarCopy {
    readonly selected_count_one: string;
    readonly selected_count_other: string;
}

/**
 * Localizes `atom-bulk-action-bar`, whose defaults are English on both ends.
 *
 * `dismissLabel` names the bar's built-in X, which is also what emits
 * `(dismissed)` — it IS the "Deselect" action, so it reuses the existing global
 * copy instead of introducing a fourth button.
 *
 * `getSelectedLabel` returns the WORDING ONLY, never the number. The bar prints
 * `count` itself, in a span of its own so the figure can carry more weight than
 * the words beside it; a formatter that interpolates the count as well makes the
 * bar read "2 2 seleccionadas". The parameter is here to pick the plural form,
 * which is why the catalog keys carry no `{{count}}`. A screen with its own noun
 * still wins through the bar's `countLabel` input — Clientes says "clientes
 * seleccionados" — and every other bar lands here.
 *
 * A subscription rather than an `effect`: it does not depend on when effects are
 * scheduled for a root-provided service, and `selectTranslate` waits for the new
 * bundle. `langChanges$` fires while that bundle is still loading, so translating
 * at that instant resolves against the previous language.
 */
@Injectable({
    providedIn: "root",
})
export class AtomBulkActionBarIntlService extends AtomBulkActionBarIntl {
    private readonly translocoService = inject(TranslocoService);

    constructor() {
        super();
        this.translocoService.langChanges$
            .pipe(
                // switchMap so switching twice in a row drops the first wait.
                switchMap(() =>
                    // Both sources are driven by `langChanges$` themselves, so
                    // subscribing to them side by side would notify the bar
                    // twice per switch. `combineLatest` waits for the pair and
                    // `take(1)` closes it, which leaves the outer `langChanges$`
                    // as the only trigger. `take(1)` belongs here and not on the
                    // outer chain, where it would localize the first language
                    // and then unsubscribe for good.
                    combineLatest([
                        this.translocoService.selectTranslateObject<BulkActionBarCopy>(
                            "atom_bulk_action_bar",
                        ),
                        this.translocoService.selectTranslate<string>(
                            "labels.deselect",
                        ),
                    ]).pipe(take(1)),
                ),
                takeUntilDestroyed(),
            )
            .subscribe(([copy, dismissLabel]) => {
                this.getSelectedLabel = (count: number): string =>
                    count === 1
                        ? copy.selected_count_one
                        : copy.selected_count_other;
                this.dismissLabel = dismissLabel;
                this.changes.next();
            });
    }
}
